import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BtnTeal, SetSec } from '@quizzach/styles';
import { validateInput } from '@quizzach/utils';
import { InlineAlertMessage } from '@quizzach/components';
import { Edit, IcClose } from '@quizzach/assets';
import { useProfile } from '@quizzach/api-hooks';
import { useAuth } from '@quizzach/contexts';

export const UserSettings = () => {
	const { t } = useTranslation();
	const { profileInfo } = useAuth();

	const [inputValue, setInputValue] = useState('');
	const [syntaxError, setSyntaxError] = useState<string | null>(null);
	const [displayedError, setDisplayedError] = useState<string | null>(null);
	const [backendError, setBackendError] = useState<string | null>(null);
	const { error, errorCode, updateProfile } = useProfile();
	const [disableSubmit, setDisableSubmit] = useState(true);

	const [editUsername, setEditUsername] = useState(false);

	const handleOpenEdit = () => {
		setEditUsername(!editUsername);
		setSyntaxError(null);
		setBackendError(null);
		setInputValue(profileInfo?.nickname ?? '');
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setInputValue(value);
		const error = validateInput('username', value);
		setSyntaxError(error);
		setBackendError(null);
	};

	const handleSubmit = () => {
		updateProfile({ nickname: inputValue });
		if (!error) {
			handleOpenEdit();
		}
	};

	/* the disabled submit already covers the empty field, so that case shows
	   no warning; other syntax warnings wait out a pause in typing */
	useEffect(() => {
		if (!syntaxError || inputValue.trim() === '') {
			setDisplayedError(null);
			return;
		}
		const timer = setTimeout(() => setDisplayedError(syntaxError), 900);
		return () => clearTimeout(timer);
	}, [syntaxError, inputValue]);

	useEffect(() => {
		if (syntaxError) {
			setDisableSubmit(true);
		} else if (profileInfo?.nickname === inputValue) {
			setDisableSubmit(true);
		} else setDisableSubmit(false);
	}, [profileInfo, syntaxError, inputValue]);

	useEffect(() => {
		if (error) {
			if (errorCode === 422) {
				setBackendError(t('profile.settings.usernameTaken'));
			} else {
				setBackendError(t('profile.settings.genericError'));
			}
		}
	}, [error, errorCode, t]);

	useEffect(() => {
		if (profileInfo) {
			setInputValue(profileInfo.nickname);
		}
	}, [profileInfo]);

	if (!profileInfo) {
		return null;
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Escape') {
			handleOpenEdit();
		}
		if (e.key === 'Enter' && !disableSubmit) {
			handleSubmit();
		}
	};

	return (
		<SetSec data-id='user-settings'>
			<div className='rows'>
				<div
					className={`row${editUsername ? ' editing' : ''}`}
					onClick={editUsername ? undefined : handleOpenEdit}
				>
					<span className='lbl'>{t('profile.settings.username')}</span>
					{!editUsername && (
						<span className='edit-ic'>
							<Edit size={15} />
						</span>
					)}
					{editUsername && (
						<div className='in-row'>
							<input
								name='username'
								value={inputValue}
								onChange={handleInputChange}
								onKeyDown={handleKeyDown}
								autoFocus
							/>
							<BtnTeal
								type='button'
								className='save'
								$small
								onClick={handleSubmit}
								disabled={disableSubmit}
							>
								{t('profile.settings.save')}
							</BtnTeal>
							<button
								type='button'
								className='x'
								onClick={handleOpenEdit}
								aria-label={t('confirmation.cancel')}
							>
								<span className='x-ic'>
									<IcClose size={14} />
								</span>
								<span className='x-txt'>{t('confirmation.cancel')}</span>
							</button>
						</div>
					)}
				</div>
				{editUsername && (displayedError || backendError) && (
					<div className='msgs'>
						{displayedError && (
							<InlineAlertMessage message={displayedError} type='warning' />
						)}
						{backendError && (
							<InlineAlertMessage message={backendError} type='error' />
						)}
					</div>
				)}
			</div>
		</SetSec>
	);
};
